import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
    Home,
    BusinessCenter,
    PeopleAlt,
    ChatBubbleOutline,
    CalendarToday,
    Search,
    AccountTree,
    Route as RouteIcon,
    Description,
    EventNote,
    FactCheck,
    Language,
    Store,
    PeopleOutline,
    HelpCenter,
    EventAvailable,
    People,
    WatchLater,
    AttachMoney,
    Message,
    CreditCard,
    Sms,
    Tune,
    Work,
    AlternateEmail,
    Person,
    PostAdd,
    Schema,
    NotificationsOutlined,
    ReportProblem,
    Logout,
    ApprovalOutlined,
} from '@mui/icons-material';
import { useUserContext, PermissionType } from 'UserContext';
import { useFeatureFlag, FeatureFlag, consoleLogInDev } from 'shared';
import { getOfferStats } from 'recruit/api/offers';
import { DropdownMenuItem } from 'library/ui';
import { Layout } from './Layout';
import { NavItem, AppMode } from './LayoutSidebar';
import { NCVacanciesCard } from './NCVacanciesCard';
import { ContactInfo } from './ContactInfo';

const RECRUIT_NAV: NavItem[] = [
    { label: 'Home', icon: <Home />, route: '/recruit' },
    { label: 'Positions', icon: <BusinessCenter />, route: '/recruit/positions' },
    { label: 'Applications', icon: <PeopleAlt />, route: '/recruit/applications' },
    { label: 'Messenger', icon: <ChatBubbleOutline />, route: '/recruit/messenger' },
    { label: 'Interviews', icon: <CalendarToday />, route: '/recruit/interviews' },
    { label: 'Sourcing', icon: <Search />, route: '/recruit/sourcing' },
];

const RECRUIT_SETTINGS_NAV: NavItem[] = [
    { label: 'Position Templates', icon: <AccountTree />, route: '/recruit/settings/templates' },
    { label: 'Screening Questions', icon: <HelpCenter />, route: '/recruit/settings/screening-questions' },
    { label: 'Workflows', icon: <RouteIcon />, route: '/recruit/settings/workflows' },
    { label: 'Scorecards', icon: <FactCheck />, route: '/recruit/settings/scorecards' },
    { label: 'Communication Templates', icon: <Description />, route: '/recruit/settings/communication' },
    { label: 'Offer Templates', icon: <PostAdd />, route: '/recruit/settings/offer-templates' },
    { label: 'Approval Flows', icon: <Schema />, route: '/recruit/settings/approval-flows' },
    { label: 'Interview Settings', icon: <EventNote />, route: '/recruit/settings/interviews' },
    { label: 'Careers Page', icon: <Language />, route: '/recruit/settings/careers' },
    { label: 'Billing', icon: <CreditCard />, route: '/recruit/settings/billing' },
    {
        label: 'Set Up',
        icon: <Tune />,
        route: '/recruit/settings/setup',
        children: [
            { label: 'SMS Setup', icon: <Sms />, route: '/recruit/settings/sms' },
            { label: 'Indeed Setup', icon: <Work />, route: '/recruit/settings/indeed' },
            { label: 'Reply-to Emails', icon: <AlternateEmail />, route: '/recruit/settings/emails' },
        ],
    },
];

const SUBS_NAV: NavItem[] = [
    { label: 'Home', icon: <Home />, route: '/home' },
    { label: 'My Jobs', icon: <EventAvailable />, route: '/jobs' },
    { label: 'My Staff', icon: <People />, route: '/providers' },
    { label: 'Timesheets', icon: <WatchLater />, route: '/timesheets' },
    { label: 'Payments', icon: <AttachMoney />, route: '/payments' },
    { label: 'Messages', icon: <Message />, route: '/messages' },
];

const BOTTOM_NAV: NavItem[] = [
    { label: 'Manage Centers', icon: <Store />, route: '/manage-centers' },
    { label: 'Manage Users', icon: <PeopleOutline />, route: '/manage-users' },
];

function detectModeFromPath(pathname: string): AppMode | null {
    if (pathname.startsWith('/recruit')) return 'recruit';
    if (SUBS_NAV.some((item) => pathname === item.route || pathname.startsWith(`${item.route}/`))) return 'subs';
    return null;
}

export function BusinessLayout({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    const history = useHistory();
    const { user, logout, hasPermission, hasRecruitAccess, hasSubsAccess } = useUserContext();
    const { featureActive: recruitLayoutActive } = useFeatureFlag(FeatureFlag.RECRUIT);
    const { featureActive: scorecardsActive } = useFeatureFlag(FeatureFlag.RECRUIT_SCORECARDS);
    const { featureActive: offersActive } = useFeatureFlag(FeatureFlag.RECRUIT_OFFERS);
    const hasRecruit = recruitLayoutActive && hasRecruitAccess();
    const [offerActionCount, setOfferActionCount] = useState(0);
    const hasSubs = hasSubsAccess();
    const recruitOnly = hasRecruit && !hasSubs;
    const showRecruitToggle = hasRecruit && hasSubs;

    const [mode, setMode] = useState<AppMode>(() => detectModeFromPath(location.pathname) ?? 'subs');

    useEffect(() => {
        const detected = detectModeFromPath(location.pathname);
        if (detected) setMode(detected);
    }, [location.pathname]);

    useEffect(() => {
        if (recruitOnly && detectModeFromPath(location.pathname) === 'subs') {
            history.replace('/recruit');
        }
    }, [recruitOnly, location.pathname]);

    const currentMode: AppMode = showRecruitToggle ? mode : recruitOnly ? 'recruit' : 'subs';
    const isRecruit = currentMode === 'recruit';

    useEffect(() => {
        if (!hasRecruit || !offersActive) return;
        getOfferStats()
            .then((stats) => setOfferActionCount(stats.action_required))
            .catch(consoleLogInDev);
    }, [hasRecruit, offersActive, location.pathname]);

    const recruitNav = offersActive
        ? RECRUIT_NAV.flatMap((item) =>
              item.route === '/recruit/applications'
                  ? [
                        item,
                        {
                            label: 'Offers',
                            icon: <ApprovalOutlined />,
                            route: '/recruit/offers',
                            badge: offerActionCount,
                        },
                    ]
                  : [item],
          )
        : RECRUIT_NAV;

    const recruitSettingsNav = RECRUIT_SETTINGS_NAV.filter(
        (item) => item.route !== '/recruit/settings/billing' || hasPermission(PermissionType.ManagePayments),
    )
        .filter((item) => item.route !== '/recruit/settings/scorecards' || scorecardsActive)
        .filter((item) => item.route !== '/recruit/settings/offer-templates' || offersActive)
        .filter((item) => item.route !== '/recruit/settings/approval-flows' || offersActive);

    const bottomNav = BOTTOM_NAV.filter(
        (item) => item.route !== '/manage-users' || hasPermission(PermissionType.ManageAccount),
    );

    const isNorthCarolinaCenter = user?.address?.some((addr) => addr.state === 'North Carolina') ?? false;
    const initials = user ? `${user.first_name[0] || ''}${user.last_name[0] || ''}`.toUpperCase() : '';

    const handleModeChange = (newMode: AppMode) => {
        if (newMode === 'recruit') {
            history.push('/recruit');
        } else {
            history.push('/home');
        }
    };

    const accountMenuItems: DropdownMenuItem[] = [
        { label: 'My Account', icon: <Person fontSize="small" />, to: '/profile' },
        { label: 'Notifications', icon: <NotificationsOutlined fontSize="small" />, to: '/notifications' },
        { label: 'Submit a Report', icon: <ReportProblem fontSize="small" />, to: '/reports/submit' },
        {
            label: 'Sign Out',
            icon: <Logout fontSize="small" />,
            onClick: () => logout('/login'),
            id: 'sign-out-button',
        },
    ];

    return (
        <Layout
            initials={initials}
            avatarImageUrl={user?.profile_picture?.image}
            navItems={isRecruit ? recruitNav : SUBS_NAV}
            bottomNavItems={bottomNav}
            settingsNavItems={isRecruit ? recruitSettingsNav : undefined}
            modeToggle={showRecruitToggle ? { mode: currentMode, onModeChange: handleModeChange } : undefined}
            accountMenuItems={accountMenuItems}
            showNotificationBell
            extraBottomContent={
                <>
                    {isNorthCarolinaCenter && <NCVacanciesCard />}
                    <ContactInfo />
                </>
            }
        >
            {children}
        </Layout>
    );
}
